import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import type SchoolYear from "@shared/models/school-year"
import { ALL_SCHOOL_YEAR_IDS, CURRENT_SCHOOL_YEAR_ID } from "@shared/models/school-year"
import { handleError } from "cc-frontend/error-handling"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import groupViewAction from "cc-frontend/lib/actions/group/GROUP_VIEW"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { isGroupMembershipGroup } from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { filter, find } from "lodash-es"
import { tracked } from "tracked-built-ins"

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolShow extends Component<Signature> {
  @service declare modalManager: ModalManager
  @service declare dialog: Dialog
  @service declare rpc: Rpc
  @service declare session: Session
  @service declare dispatcher: Dispatcher

  @tracked selectedSchoolYearId = CURRENT_SCHOOL_YEAR_ID
  @tracked nameOfNewTeam = null
  @tracked isCreatingTeam = false
  schoolYearIds = ALL_SCHOOL_YEAR_IDS

  @use schoolYears = new FindManyAltogether(() => ["school-year", this.schoolYearIds as string[]])

  get teamMemberships() {
    return filter(get(this.args.group.attributes, "memberships"), isGroupMembershipGroup)
  }

  get selectedSchoolYear() {
    return find(this.schoolYears.content, (el) => el.id === this.selectedSchoolYearId)
  }

  @action
  selectSchoolYear(schoolYear: SchoolYear) {
    this.selectedSchoolYearId = schoolYear.id
  }

  @action
  addTeam() {
    this.dialog
      .blank("dialogs/ask-for-team-title", {
        className: "bold-dialog",
      })
      .then((nameOfNewTeam: string) => {
        this.isCreatingTeam = true
        this.rpc
          .group_create_team({
            groupId: this.args.group.id,
            title: nameOfNewTeam,
          })
          .then((resp) => {
            if (resp.responseOk) {
              this.isCreatingTeam = false
              this.nameOfNewTeam = null
              this.modalManager.openModal("group/create-team", {
                parentGroup: this.args.group,
                groupId: resp.result.newGroupId,
              })
            } else {
              console.error("Group failed to create")
            }
          })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action
  showSetup() {
    this.modalManager.openModal("school/setup", {
      school: this.args.group,
    })
  }

  trackView = () => {
    if (this.isDestroyed || this.isDestroying) return

    this.dispatcher.dispatchPreparedAction(groupViewAction({ group: this.args.group }))
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "School::Show": typeof SchoolShow
  }
}
